import axios from '../../axios'
import Axios, { AxiosRequestConfig } from 'axios'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { toast } from 'react-toastify'
import { Folder, FolderItem } from './types'
import { OBJECT_TYPE } from 'app/constants'
import { projectsStore } from './projects.store'
import { predictorsStore } from './predictors.store'
import { mpoProjectsStore } from './mpoProjects.store'
import { datasetsStore } from './datasets.store'
import { fileStorages } from './fileStorages.store'

export class FoldersStore {
  loadingList: boolean = false
  deleting: boolean = false
  list: Folder[] = []
  entries: {
    [key: string]: Folder
  } = {}
  totalPages: number = 0
  totalElements: number = 0
  cancelTokenSource = () => {}
  foldersInfoEnteries: {
    [type: string]: {
      [objectId: number]: Pick<Folder, 'id' | 'name'>
    }
  } = {}

  constructor() {
    makeObservable(this, {
      loadingList: observable,
      deleting: observable,
      list: observable,
      entries: observable,
      totalPages: observable,
      totalElements: observable,
      foldersInfoEnteries: observable,
      getData: action,
      delete: action,
      setFolder: action,
      reset: action,
      clearList: action,
      removeFromList: action,
      setLoadingList: action,
    })
  }

  reset = () => {
    this.clearList()
    this.deleting = false
    this.entries = {}
    this.cancelTokenSource = () => {}
    this.foldersInfoEnteries = {}
  }

  clearList = () => {
    this.loadingList = false
    this.list = []
    this.totalPages = 0
    this.totalElements = 0
  }

  setLoadingList = (value: boolean) => {
    this.loadingList = value
  }

  removeFromList = (id: number) => {
    this.list = this.list.filter((item) => item.id !== id)
  }

  create = async (data: object) => {
    return await axios
      .post('/prediction-services/api/v1/folder', data)
      .then((response) => {
        toast.success('Created successfully')
        return response
      })
  }

  update = async (id: number, data: object) => {
    return await axios
      .put('/prediction-services/api/v1/folder/' + id, data)
      .then((response) => {
        toast.success('Updated successfully')
        return response
      })
  }

  delete = async (id: number) => {
    this.deleting = true
    await axios
      .delete('/prediction-services/api/v1/folder/' + id)
      .then(({ data }) => {
        if (data.status === 'DELETING') {
          toast.success(
            'Delete is started. You will be notified when the process is completed'
          )
          this.setFolder(data)
        } else if (data.status === 'DELETED') {
          toast.success('Deleted successfully')
          this.removeFromList(id)
        }
      })
      .finally(() => {
        this.deleting = false
      })
  }

  getById = async (id: number) => {
    return await axios(`/prediction-services/api/v1/folder/${id}`).then(
      ({ data }) => {
        this.setFolder(data)
        return data
      }
    )
  }

  getData = (filters: any = {}) => {
    if (typeof this.cancelTokenSource == 'function') {
      // this.cancelTokenSource()
    }
    const cancelTokenSource = Axios.CancelToken.source()

    const params: any = {
      ...filters,
      name: filters.searchValue,
      access_filter: filters.sharingStatus,
    }
    delete params.searchValue
    delete params.ownership
    delete params.sharingStatus

    this.setLoadingList(true)

    const config: AxiosRequestConfig = {
      cancelToken: cancelTokenSource.token,
      params,
    }
    axios
      .get(`/prediction-services/api/v1/folder`, config)
      .then(({ data }) => {
        if (data?.content) {
          runInAction(() => {
            this.list = data.content
            this.totalPages = data.totalPages
            this.totalElements = data.totalElements
            this.list.forEach((item) => (this.entries[`${item.id}`] = item))
          })
        }
      })
      .catch(() => {
        this.clearList()
      })
      .finally(() => {
        this.setLoadingList(false)
      })

    this.cancelTokenSource = cancelTokenSource.cancel
  }

  setFolder = (folder: Folder) => {
    const index = this.list.findIndex((item: Folder) => item.id === folder.id)
    if (index !== -1) {
      this.list[index] = folder
    }
    this.entries[`${folder.id}`] = folder
  }

  getLatestFolders = async () => {
    const { data } = await axios.get(
      `/prediction-services/api/v1/folder?sort=creationDate,DESC&size=${3}&access_filter=OWNED`
    )
    return data;
  }

  searchFolderByName = async (name: string, cancelTokenSource?: any) => {
    const { data } = await axios.get(
      `/prediction-services/api/v1/folder?name=${encodeURIComponent(
        name
      )}&size=${5}&access_filter=OWNED`,
      {
        // @ts-ignore
        cancelTokenSource,
      }
    )
    return data.content
  }

  getFolderInfo = async (
    objectType: FolderItem['type'],
    objectId: FolderItem['objectId']
  ) => {
    return await axios(
      `/prediction-services/api/v1/item/${objectType}/${objectId}`,
      // @ts-ignore
      { withoutErrorToast: true }
    ).then(({ data }) => {
      /* Checking if the type is not foldersInfoEnteries, set it to empty object. */
      if (!this.foldersInfoEnteries[objectType]) {
        this.foldersInfoEnteries[objectType] = {}
      }
      this.foldersInfoEnteries[objectType][objectId] = data

      return data
    })
  }

  // To Refetch objects data in list view
  refreshListObjectsData = async (
    objectType: FolderItem['type'],
    objectIds: number[],
    projectType: string
  ) => {
    switch (objectType) {
    case OBJECT_TYPE.PROJECT:
      return await projectsStore.refetchListIdsWithType(objectIds, projectType)
     case OBJECT_TYPE.PREDICTOR:
        return await predictorsStore.refetchListIds(objectIds)
    case OBJECT_TYPE.MPO_PROJECT:
      return await mpoProjectsStore.refetchListIds(objectIds)
    case OBJECT_TYPE.DATASET:
      return await datasetsStore.refetchListIds(objectIds)
      case OBJECT_TYPE.FILE_STORAGE:
        return await fileStorages.refetchListIds(objectIds)
    }
  }

  createFolderItems = async (
    folderId: number,
    items: Pick<FolderItem, 'type' | 'objectId' | 'order' | 'ownerUserId'>[],
    projectType: string
  ) => {
    return await axios
      .post('/prediction-services/api/v1/item', {
        folderId: folderId,
        items,
      })
      .then((response) => {
        // Refetch object to refersh FolderName component
        items.forEach(({ type, objectId }) => {
          this.getFolderInfo(type, objectId)
          this.refreshListObjectsData(type, [objectId], projectType)
        })
        return response
      })
  }
  deleteFolderItem = async (
    objectType: FolderItem['type'],
    objectId: FolderItem['objectId'],
    projectType: string
  ) => {
    return await axios
      .delete(`/prediction-services/api/v1/item/${objectType}/${objectId}`)
      .then(({ data }) => {
        // Refetch object to refersh FolderName component
        this.getFolderInfo(objectType, objectId)
        this.refreshListObjectsData(objectType, [objectId], projectType)
        return data
      })
  }
}

export const foldersStore = new FoldersStore()
